// Isomorphic logger wrapper: client → console, server → pino (logger-core.node)
// This module stays client-safe but seamlessly upgrades to Pino on the server.
import type { Logger as PinoLogger } from 'pino';

import { env } from '@/env';
import { readSystemEnvString } from './utils/env';

type Fields = Record<string, unknown>;
export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal' | 'silent';

type ConsoleLevel = 'info' | 'error' | 'warn' | 'debug';

export interface LoggerLike {
  debug: (message: string, data?: unknown) => void;
  error: (message: string, data?: unknown) => void;
  flush: () => Promise<void>;
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  with: (fields: Fields) => LoggerLike;
}

import { isRecord } from '@/lib/utils/type-guards';

const normalizeLogData = (data: unknown): Fields => {
  if (isRecord(data)) {
    return data;
  }
  if (data === undefined) {
    return {};
  }
  return { data };
};

function adapt(p: PinoLogger): LoggerLike {
  const emit = (level: 'info' | 'error' | 'warn' | 'debug', message: string, data?: unknown) => {
    const merged = normalizeLogData(data);
    const methodMap: Record<typeof level, (payload: Fields, msg: string) => void> = {
      info(payload, msg) {
        p.info(payload, msg);
      },
      error(payload, msg) {
        p.error(payload, msg);
      },
      warn(payload, msg) {
        p.warn(payload, msg);
      },
      debug(payload, msg) {
        p.debug(payload, msg);
      },
    };
    methodMap[level](merged, message);
  };
  return {
    info: (m, d) => {
      emit('info', m, d);
    },
    error: (m, d) => {
      emit('error', m, d);
    },
    warn: (m, d) => {
      emit('warn', m, d);
    },
    debug: (m, d) => {
      emit('debug', m, d);
    },
    with: (fields: Fields) => adapt(p.child(fields)),
    flush: async () => {
      // oxlint-disable-next-line typescript-eslint/no-confusing-void-expression
      const flushResult = p.flush();
      if (!isPromiseLike(flushResult)) {
        return;
      }
      await flushResult;
    },
  };
}

// Client console-backed logger (lightweight)
const logToConsole = (level: ConsoleLevel, message: string, data?: unknown) => {
  const consoleTarget = globalThis.console;
  const fallback = consoleTarget?.[level];
  if (typeof fallback !== 'function') {
    return;
  }
  if (data === undefined) {
    fallback.call(consoleTarget, message);
  } else {
    fallback.call(consoleTarget, message, data);
  }
};

const consoleLogger: LoggerLike = {
  info: (m, d) => {
    logToConsole('info', m, d);
  },
  error: (m, d) => {
    logToConsole('error', m, d);
  },
  warn: (m, d) => {
    logToConsole('warn', m, d);
  },
  debug: (m, d) => {
    logToConsole('debug', m, d);
  },
  with: (fields: Fields) => createLoggerChild(consoleLogger, fields),
  flush: async () => {
    // Console logger does not buffer output; nothing to flush.
  },
};

function createLoggerChild(parent: LoggerLike, fields: Fields): LoggerLike {
  const emit = (level: 'info' | 'error' | 'warn' | 'debug', message: string, data?: unknown) => {
    const payload = normalizeLogData(data);
    parent[level](message, { ...fields, ...payload });
  };
  return {
    info: (m, d) => {
      emit('info', m, d);
    },
    error: (m, d) => {
      emit('error', m, d);
    },
    warn: (m, d) => {
      emit('warn', m, d);
    },
    debug: (m, d) => {
      emit('debug', m, d);
    },
    with: (additional) => createLoggerChild(parent, { ...fields, ...additional }),
    flush: parent.flush,
  };
}

let _setLogLevel: (level: LogLevel) => void = () => {
  // noop placeholder until logger is initialized
};
let _getLogLevel: () => LogLevel = () => 'info';
let _flushAll: () => Promise<void> = async () => {
  // noop placeholder until logger is initialized
};

// Export a LIVE binding so switching console → Pino updates all consumers.
export let logger: LoggerLike = consoleLogger;
let usingConsoleLogger = true;

// Logger factory with caching for performance
const loggerCache = new Map<string, LoggerLike>();

function rehydrateLoggerCache(): void {
  for (const [key] of loggerCache) {
    loggerCache.set(key, logger.with({ module: key }));
  }
}

// Live child wrapper that forwards to the current base logger each call.
function liveChild(baseFields: Fields): LoggerLike {
  const emit = (level: 'info' | 'error' | 'warn' | 'debug', message: string, data?: unknown) => {
    const payload = normalizeLogData(data);
    // Forward to the current logger binding with merged fields
    const child = logger.with(baseFields);
    const logMethods: Record<typeof level, (message_: string, dataObject: unknown) => void> = {
      info(message_, dataObject) {
        child.info(message_, dataObject);
      },
      error(message_, dataObject) {
        child.error(message_, dataObject);
      },
      warn(message_, dataObject) {
        child.warn(message_, dataObject);
      },
      debug(message_, dataObject) {
        child.debug(message_, dataObject);
      },
    };
    logMethods[level](message, payload);
  };
  return {
    info: (m, d) => {
      emit('info', m, d);
    },
    error: (m, d) => {
      emit('error', m, d);
    },
    warn: (m, d) => {
      emit('warn', m, d);
    },
    debug: (m, d) => {
      emit('debug', m, d);
    },
    with: (fields: Fields) => liveChild({ ...baseFields, ...fields }),
    flush: async () => {
      await _flushAll();
    },
  };
}

export function createLogger(module: string): LoggerLike {
  // Return a live child; avoids stale references when backend upgrades to Pino
  return liveChild({ module });
}

export { serializeError } from '@/lib/utils';

const forceConsoleFlag = readSystemEnvString('SWEETISTICS_FORCE_CONSOLE_LOGGER');
const shouldForceConsoleLogger =
  typeof forceConsoleFlag === 'string' && ['true', '1', 'yes'].includes(forceConsoleFlag.toLowerCase());

const NEXT_RUNTIME = env.NEXT_RUNTIME ?? null;
const isEdgeRuntime = NEXT_RUNTIME === 'edge';

if (globalThis.window === undefined && !shouldForceConsoleLogger && !isEdgeRuntime) {
  // Lazy-load Node logger only on the server to keep this module client-safe
  try {
    const m = await import('./logger-core.node');
    logger = adapt(m.logger);
    usingConsoleLogger = false;
    _setLogLevel = m.setLogLevel;
    _getLogLevel = m.getLogLevel;
    _flushAll = m.flushAllStreams;
    rehydrateLoggerCache();
  } catch {
    // Keep console fallback; no-op
  }
}

// Backward compatibility - keep existing exports but use factory
export const dbLogger = createLogger('database');
export const authLogger = createLogger('auth');
export const apiLogger = createLogger('api');
export const cacheLogger = createLogger('cache');
export const twitterLogger = createLogger('twitter');
export const timelineLogger = createLogger('timeline');
export const socialLogger = createLogger('social');
export const archiveLogger = createLogger('archive');
export const aiLogger = createLogger('ai');
export const analysisLogger = createLogger('analysis');
export const embeddingLogger = createLogger('embedding');
export const emailLogger = createLogger('email');
export const subscriptionLogger = createLogger('subscription');
export const syncLogger = createLogger('sync');
export const searchLogger = createLogger('search');
export const uiLogger = createLogger('ui');
export const hookLogger = createLogger('hook');
export const fetchLogger = createLogger('fetch');
export const adminLogger = createLogger('admin');
export const queueLogger = createLogger('queue');
export const utilsLogger = createLogger('utils');
export const turbopufferLogger = createLogger('turbopuffer');
export const featuresLogger = createLogger('features');
export const middlewareLogger = createLogger('middleware');
export const requestLogger = createLogger('request');
export const serviceLogger = createLogger('service');

// Export flush function for API routes and server components
export const flushLogs = async () => {
  await _flushAll();
};
export const setLogLevel = (l: LogLevel) => {
  _setLogLevel(l);
};
export const getLogLevel = (): LogLevel => _getLogLevel();

export const isConsoleLogger = (): boolean => usingConsoleLogger;
const isPromiseLike = (value: unknown): value is PromiseLike<unknown> =>
  Boolean(
    value && typeof value === 'object' && 'then' in value && typeof (value as { then?: unknown }).then === 'function'
  );
